import { useQuery } from '@tanstack/react-query';
import { KnownAccessControls } from 'app/business-logic/security/KnownAccessControls';
import { QUERY_KEY } from 'app/business-logic/services/query-key';
import {
  getNoiseEventsByAlertIdClassification,
  getNoiseEventsByAlertIdResourceGroups,
  getNoiseEventsByAlertIdBase,
} from 'app/business-logic/services/noise-service/getNoiseEventsByAlertId';
import { useHasAccess } from 'app/hooks/use-has-access';
import { useMemo } from 'react';

export const useEventsByAlertId = (alertId: string | undefined) => {
  const hasAccess = useHasAccess();
  const hasResourceGroupsAccess = hasAccess(KnownAccessControls.app.config.resourceGroups._);
  const hasClassificationAccess = hasAccess(KnownAccessControls.app.noise.classification.data.read._);
  const isEnabled = !!alertId;

  const {
    data: baseData = [],
    isFetching: isBaseFetching,
    isLoading: isBaseLoading,
  } = useQuery({
    queryKey: [QUERY_KEY.noiseEventsByAlertIdBase, alertId],
    queryFn: () => {
      if (!alertId) return;
      return getNoiseEventsByAlertIdBase(alertId);
    },
    enabled: isEnabled,
    retry: false,
  });

  const { data: resourceGroupsData, isFetching: isResourceGroupsFetching } = useQuery({
    queryKey: [QUERY_KEY.noiseEventsByAlertIdResourceGroups, alertId],
    queryFn: () => {
      if (!alertId) return;
      return getNoiseEventsByAlertIdResourceGroups(alertId);
    },
    enabled: isEnabled && hasResourceGroupsAccess,
    retry: false,
  });

  const { data: classificationData, isFetching: isClassificationFetching } = useQuery({
    queryKey: [QUERY_KEY.noiseEventsByAlertIdClassification, alertId],
    queryFn: () => {
      if (!alertId) return;
      return getNoiseEventsByAlertIdClassification(alertId);
    },
    enabled: isEnabled && hasClassificationAccess,
    retry: false,
  });

  const combinedData = useMemo(() => {
    if (!baseData?.length) return undefined;
    return baseData.flatMap(baseEvent => {
      if (!baseEvent) return [];
      const resourceGroupsInfo = resourceGroupsData?.find(r => r?.id === baseEvent.id)?.noiseEventPolicyOverride;
      const classificationInfo = classificationData?.find(r => r?.id === baseEvent.id);
      return {
        ...baseEvent,
        noiseEventPolicyOverride: resourceGroupsInfo ?? [],
        classifications: classificationInfo?.classifications ?? [],
        comments: classificationInfo?.comments ?? null,
        disregarded: classificationInfo?.disregarded ?? false,
      };
    });
  }, [baseData, resourceGroupsData, classificationData]);

  return {
    data: combinedData,
    isFetching: isBaseFetching,
    isLoading: isBaseLoading,
    isResourceGroupsFetching: hasResourceGroupsAccess && isResourceGroupsFetching,
    isClassificationFetching: hasClassificationAccess && isClassificationFetching,
  };
};
